import React from "react";
import { useResolvedRecordingMedia } from "./useResolvedRecordingMedia";
import SpectrogramPanel from "./SpectrogramPanel";

const PLAYBACK_RATE_OPTIONS = [0.5, 1, 2, 4];

function buildPlaybackRateLabel(rate) {
  return `${rate}x`;
}

function PlaybackRateButtons({ activeRate, onChange }) {
  return (
    <div className="flex flex-wrap gap-2">
      {PLAYBACK_RATE_OPTIONS.map((rate) => {
        const active = activeRate === rate;

        return (
          <button
            key={rate}
            type="button"
            className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
              active
                ? "border-primary bg-primary text-white"
                : "border-greyscale-1 bg-background text-typography hover:border-primary"
            }`}
            onClick={() => onChange(rate)}
          >
            {buildPlaybackRateLabel(rate)}
          </button>
        );
      })}
    </div>
  );
}

export default function RecordingViewerSection({
  title,
  variant,
  measurement,
  diagnosticContext = null,
  emptyLabel,
  loadingLabel,
  errorLabel,
  videoTitle,
  videoSubtitle,
  audioTitle,
  audioSpectrumTitle,
  audioSpectrumSubtitle,
  audioSpectrumLoading,
  audioSpectrumHint,
  audioSpectrumUnavailable,
  showSpectrogramPanel = true,
}) {
  const {
    loadingMedia,
    mediaError,
    mediaRef,
    mediaSource,
    playbackSourceType,
    playbackRate,
    resolvedMediaUrl,
    setPlaybackRate,
  } = useResolvedRecordingMedia(variant, measurement, diagnosticContext);

  if (!mediaSource.kind) {
    return (
      <section className="rounded-xl border border-greyscale-1 bg-container p-4">
        <h3 className="text-lg font-semibold text-typography">{title}</h3>
        <div className="mt-4 rounded-lg bg-background px-4 py-10 text-center text-sm text-greyscale-3">
          {emptyLabel}
        </div>
      </section>
    );
  }

  if (loadingMedia) {
    return (
      <section className="rounded-xl border border-greyscale-1 bg-container p-4">
        <h3 className="text-lg font-semibold text-typography">{title}</h3>
        <div className="mt-4 rounded-lg bg-background px-4 py-10 text-center text-sm text-greyscale-3">
          {loadingLabel}
        </div>
      </section>
    );
  }

  if (!resolvedMediaUrl || mediaError) {
    return (
      <section className="rounded-xl border border-greyscale-1 bg-container p-4">
        <h3 className="text-lg font-semibold text-typography">{title}</h3>
        <div className="mt-4 rounded-lg bg-background px-4 py-10 text-center text-sm text-greyscale-3">
          {errorLabel}
        </div>
      </section>
    );
  }

  const mediaKey = `${mediaSource.kind}-${resolvedMediaUrl}`;

  return mediaSource.kind === "video" ? (
    <div className="card flex flex-col gap-4 p-4">
      <div className="flex flex-row items-start justify-between gap-1">
        <div>
          <h4 className="subtitle-text text-typography">{videoTitle}</h4>
          {videoSubtitle ? (
            <p className="mt-1 text-sm text-greyscale-3">{videoSubtitle}</p>
          ) : null}
        </div>
        <PlaybackRateButtons
          activeRate={playbackRate}
          onChange={setPlaybackRate}
        />
      </div>

      <div className="grid gap-4 xl:grid-cols-[minmax(240px,320px)_minmax(0,1fr)]">
        <div className="flex justify-center xl:justify-start">
          <video
            key={mediaKey}
            ref={mediaRef}
            data-testid="recording-viewer-video"
            controls
            playsInline
            preload="metadata"
            className="aspect-[9/16] w-full max-w-[20rem] rounded-xl bg-black object-contain"
          >
            <source
              src={resolvedMediaUrl}
              type={playbackSourceType.declaredType || undefined}
            />
          </video>
        </div>

        {showSpectrogramPanel ? (
          <SpectrogramPanel
            title={audioSpectrumTitle}
            subtitle={audioSpectrumSubtitle}
            mediaRef={mediaRef}
            mediaUrl={resolvedMediaUrl}
            unavailableLabel={audioSpectrumUnavailable}
            loadingLabel={audioSpectrumLoading}
            hintLabel={audioSpectrumHint}
            isChild
          />
        ) : null}
      </div>
    </div>
  ) : (
    <div className="card p-4">
      <div className="flex flex-row items-start justify-between gap-1">
        <div>
          <h4 className="subtitle-text text-typography">{audioTitle}</h4>
        </div>
        <PlaybackRateButtons
          activeRate={playbackRate}
          onChange={setPlaybackRate}
        />
      </div>

      {showSpectrogramPanel ? (
        <SpectrogramPanel
          title={audioSpectrumTitle}
          subtitle={audioSpectrumSubtitle}
          mediaRef={mediaRef}
          mediaUrl={resolvedMediaUrl}
          unavailableLabel={audioSpectrumUnavailable}
          loadingLabel={audioSpectrumLoading}
          hintLabel={audioSpectrumHint}
          isChild
        />
      ) : null}

      <audio
        key={mediaKey}
        ref={mediaRef}
        data-testid="recording-viewer-audio"
        controls
        preload="metadata"
        className="mt-4 w-full rounded-xl"
      >
        <source
          src={resolvedMediaUrl}
          type={playbackSourceType.declaredType || undefined}
        />
      </audio>
    </div>
  );
}
